import { IndexedDbFactory } from '../factories/indexeddb-provider.factory';
import type { IBankRequestCacheRepository } from '@app/domain/ports';
import { BaseIndexDbRepository } from './base-indexdb.repository';
import { BankRequestCacheEntry, CachedBankStatement } from '@app/domain/entities';

export class BankRequestCacheRepository
  extends BaseIndexDbRepository<BankRequestCacheEntry>
  implements IBankRequestCacheRepository
{
  constructor(
    indexedDbFactory: IndexedDbFactory,
    private readonly requestCacheTtlMs: number,
  ) {
    super(indexedDbFactory, 'bank_request_cache');
  }

  /**
   * Find all cache entries for a specific source and account
   */
  async findAllBySourceAndAccount(source: string, accountId: string): Promise<BankRequestCacheEntry[]> {
    return this.executeStoreOperation('findAllBySourceAndAccount', { source, accountId }, async (db) => {
      return db.bank_request_cache.where('[source+accountId]').equals([source, accountId]).toArray();
    });
  }

  /**
   * Delete expired cache entries (older than 7 days).
   * Uses the 'fetchedAt' index for efficient querying.
   * @returns Number of deleted entries
   */
  async deleteExpired(): Promise<number> {
    return this.executeStoreOperation('deleteExpired', {}, async (db) => {
      const expiryThreshold = Date.now() - this.requestCacheTtlMs;

      const expiredEntries = await db.bank_request_cache.where('fetchedAt').belowOrEqual(expiryThreshold).toArray();

      return this.deleteKeys(
        { delete: (key: string) => db.bank_request_cache.delete(key) },
        expiredEntries.map((entry) => entry.id),
      );
    });
  }

  /**
   * Save a new cache entry for fetched statements
   */
  async saveRange(
    source: string,
    accountId: string,
    fromTimestamp: number,
    toTimestamp: number,
    statementsOrEncrypted: CachedBankStatement[] | { ciphertext: Uint8Array; iv: Uint8Array },
  ): Promise<void> {
    const id = this.generateCacheKey(source, accountId, fromTimestamp, toTimestamp);
    const entry: BankRequestCacheEntry = {
      id,
      source,
      accountId,
      fromTimestamp,
      toTimestamp,
      fetchedAt: Date.now(),
    };

    // Check if it's encrypted data or plain statements
    if (Array.isArray(statementsOrEncrypted)) {
      entry.statements = statementsOrEncrypted;
    } else {
      entry.encryptedStatements = statementsOrEncrypted;
    }

    await this.create(entry);
  }

  /**
   * Check if cached data covers the requested date range and return entries if available
   * Automatically deletes expired entries during the check
   *
   * @param source Bank integration source (e.g., 'monobank')
   * @param accountId External account ID
   * @param from Unix timestamp in seconds (inclusive)
   * @param to Unix timestamp in seconds (inclusive)
   * @returns Array of cache entries that cover the requested range, or null if coverage is incomplete
   */
  async checkCoverage(
    source: string,
    accountId: string,
    from: number,
    to: number,
  ): Promise<BankRequestCacheEntry[] | null> {
    return this.executeStoreOperation('checkCoverage', { source, accountId, from, to }, async (db) => {
      const expiryThreshold = Date.now() - this.requestCacheTtlMs;

      const potentialEntries = await db.bank_request_cache
        .where('[source+accountId]')
        .equals([source, accountId])
        .and((entry) => entry.fromTimestamp <= to)
        .toArray();

      const validEntries: BankRequestCacheEntry[] = [];
      const expiredEntryIds: string[] = [];
      for (const entry of potentialEntries) {
        if (entry.fetchedAt < expiryThreshold) {
          expiredEntryIds.push(entry.id);
        } else if (entry.toTimestamp >= from) {
          validEntries.push(entry);
        }
      }

      if (expiredEntryIds.length > 0) {
        await this.deleteKeys({ delete: (key: string) => db.bank_request_cache.delete(key) }, expiredEntryIds);
      }

      if (validEntries.length === 0) {
        return null;
      }

      validEntries.sort((a, b) => a.fromTimestamp - b.fromTimestamp);

      const mergedRanges: Array<{ from: number; to: number }> = [];

      for (const entry of validEntries) {
        if (mergedRanges.length === 0) {
          mergedRanges.push({ from: entry.fromTimestamp, to: entry.toTimestamp });
        } else {
          const lastRange = mergedRanges[mergedRanges.length - 1];

          if (entry.fromTimestamp <= lastRange.to + 1) {
            lastRange.to = Math.max(lastRange.to, entry.toTimestamp);
          } else {
            mergedRanges.push({ from: entry.fromTimestamp, to: entry.toTimestamp });
          }
        }
      }

      if (mergedRanges.length !== 1) {
        return null;
      }

      const coverage = mergedRanges[0];
      if (coverage.from > from || coverage.to < to) {
        return null;
      }

      return validEntries;
    });
  }

  /**
   * Generate composite cache key
   */
  private generateCacheKey(source: string, accountId: string, from: number, to: number): string {
    return `${source}:${accountId}:${from}:${to}`;
  }
}
